<script setup>
// NOOVI: Modal de import de cards via CSV (Gap C — fase-10).
// Importa para a pipeline atualmente aberta (pipelineId), chamando o endpoint
// namespaced pipeline/cards/import (admin-only no backend).
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Modal from '../../../../components/Modal.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import PipelineCardsAPI from 'dashboard/api/pipelineCards';
import { downloadBlob } from 'dashboard/helper/downloadHelper';

const props = defineProps({
  pipelineId: { type: [Number, String], default: null },
});

const emit = defineEmits(['imported']);
const show = defineModel('show', { type: Boolean, default: false });
const { t } = useI18n();
const { showAlert } = useAlert();

const fileInput = ref(null);
const selectedFile = ref(null);
const isImporting = ref(false);
const isDownloadingTemplate = ref(false);

// Colunas aceitas pelo import (espelham DataImport::PipelineCardManager).
const templateColumns =
  'title, stage, description, expected_revenue, contact_identifier, contact_email, contact_phone';

const downloadTemplate = async () => {
  isDownloadingTemplate.value = true;
  try {
    const { data } = await PipelineCardsAPI.downloadImportTemplate();
    downloadBlob('pipeline_cards_import_template.csv', data);
  } catch (error) {
    showAlert(error?.response?.data?.error || t('PIPELINE.IMPORT.ERROR'));
  } finally {
    isDownloadingTemplate.value = false;
  }
};

const onFileChange = () => {
  selectedFile.value = fileInput.value?.files?.[0] || null;
};

const reset = () => {
  selectedFile.value = null;
  if (fileInput.value) fileInput.value.value = null;
};

const onClose = () => {
  reset();
  show.value = false;
};

const submit = async () => {
  if (!selectedFile.value || !props.pipelineId) return;

  isImporting.value = true;
  try {
    await PipelineCardsAPI.importCards(props.pipelineId, selectedFile.value);
    showAlert(t('PIPELINE.IMPORT.SUCCESS'));
    emit('imported');
    onClose();
  } catch (error) {
    showAlert(error?.response?.data?.error || t('PIPELINE.IMPORT.ERROR'));
  } finally {
    isImporting.value = false;
  }
};
</script>

<template>
  <Modal v-model:show="show" @close="reset">
    <div class="flex flex-col gap-4 p-6">
      <h3 class="text-base font-medium text-n-slate-12">
        {{ t('PIPELINE.IMPORT.TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-11">
        {{ t('PIPELINE.IMPORT.DESCRIPTION') }}
      </p>
      <div
        class="flex flex-col gap-2 p-3 rounded-lg bg-n-alpha-1 border border-n-weak"
      >
        <div class="flex items-center justify-between gap-2">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('PIPELINE.IMPORT.TEMPLATE_TITLE') }}
          </span>
          <Button
            variant="faded"
            color="slate"
            size="sm"
            icon="i-lucide-download"
            :label="t('PIPELINE.IMPORT.DOWNLOAD_TEMPLATE')"
            :is-loading="isDownloadingTemplate"
            @click="downloadTemplate"
          />
        </div>
        <p class="text-xs text-n-slate-11">
          {{ t('PIPELINE.IMPORT.COLUMNS_HINT') }}
        </p>
        <code class="text-xs text-n-slate-12 break-words">{{
          templateColumns
        }}</code>
      </div>
      <input
        ref="fileInput"
        type="file"
        accept=".csv,text/csv"
        class="text-sm text-n-slate-12"
        @change="onFileChange"
      />
      <div class="flex justify-end gap-2">
        <Button
          variant="ghost"
          color="slate"
          :label="t('PIPELINE.IMPORT.CANCEL')"
          @click="onClose"
        />
        <Button
          :label="t('PIPELINE.IMPORT.SUBMIT')"
          :is-loading="isImporting"
          :disabled="!selectedFile || isImporting"
          @click="submit"
        />
      </div>
    </div>
  </Modal>
</template>
